import {
  Address,
  CmsBannerComponentMedia,
  CmsComponent,
  CmsResponsiveBannerComponentMedia,
  Country,
  Currency,
  Price,
  Review
} from '@spartacus/core'
import { Cart, OrderEntry } from '@spartacus/cart/base/root'

export interface SortableLabel {
  code?: string;
  name?: string;
  sortOrder?: number;
}

export type AcademicDegree = SortableLabel

export interface CMSGenericErrorMediaComponent extends CmsComponent {
  text?: string;
  buttonLink?: string;
  buttonTest?: string;
  image?: CmsResponsiveBannerComponentMedia;
  content?: string;

  // [key: string]: any;
}

export interface GalleryImageData {
  image?: string;
  legend?: string;
  videoId?: string;
  headline?: string;
}

export interface FilterOptionSelection {
  filterCode?: string;
  valueCode?: string;
  isPrimaryFilter?: boolean;
  active?: boolean;
}

export type JobTitle = SortableLabel

export interface ProductLine {
  code?: string;
  name?: string;
  priceData?: Price;
  size?: string;
  regulatoryStatus?: string;
  applications?: string;
  components?: string[];
  hasPriceOnRequest?: boolean;
  stamps?: string[];
  userManuals?: Map<string, string>;
  dataSheets?: Map<string, string>;
  entries?: any[];
  productLine?: any[];
  clinicalReferenceNumber?: string;
}

export interface ProductVariantFilterOptionData {
  code: string;
  name: string;
  urlName: string;
  active?: boolean;

  [x: string]: any;
}

export interface ProductVariantFilterData {
  code: string;
  name: string;
  filterOptions: ProductVariantFilterOptionData[];
  isPrimaryFilter?: boolean;
  prov?: boolean;
  urlName?: string;
}

export type Salutation = SortableLabel

export interface MbAzure {
  email?: string;
}

export interface MbBaseSiteCountry {
  site?: string;
  country?: string;
}

export interface MbCartMigrationContext {
  newCountryIsoCode: string;
  guid: string;
  baseSite?: boolean;
  willChangeBaseSite?: boolean;
}

export interface MbCheckoutStatus {
  checkoutPageId: string;
  checkoutType: MbCheckoutTypes;
}

export interface MbContact {
  title?: string;
  academicDegree?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  jobTitle?: string;
  customerNumber?: string;
}

export enum MbOrderDetailsInfoType {
  ADDRESS = 'address',
  CONTACT_DETAILS = 'contactDetails',
  SHIPMENT_METHOD = 'shipmentMethod',
  PAYMENT_METHOD = 'paymentMethod',
  PAYMENT_ADDITIONAL_INFO = 'paymentAdditionalInfo',
  ORDER_STATUS = 'orderStatus',
  NOTES = 'notes'
}

export interface MbReview {
  reviews?: Review[];
  writeReviewLink?: string;
  biocompareLogoUrl?: string;
  biocompareUrl?: string;
}

export interface MbProductTabReference {
  reference?: string;
}

export enum MbDatasetEntryType {
  TEXT = 'TEXT',
  PUBLICATION = 'PUBLICATION',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  PROTOCOL = 'PROTOCOL',
  HTML_TEXT = 'HTML_TEXT',
  TABLE = 'TABLE_ENTRY'
}

export interface MbDatasetEntry {
  type: MbDatasetEntryType;
}

export interface MbTextDatasetEntry extends MbDatasetEntry {
  name?: string;
  value?: string;
}

export interface MbImageDatasetEntry extends MbDatasetEntry {
  id?: string;
  url?: string;
  headline?: string;
  description?: string;
}

export interface MbVideoDatasetEntry extends MbImageDatasetEntry {
  videoId?: string;
}

export interface MbProtocolDatasetEntry extends MbDatasetEntry {
  text?: string;
  url?: string;
}

export interface MbHtmlDatasetEntry extends MbDatasetEntry {
  text?: string;
}

export interface MbTableDatasetEntry extends MbDatasetEntry {
  header?: string;
  value?: string;
}

export interface MbPublicationDatasetEntry extends MbDatasetEntry {
  name?: string;
  urls?: Map<string, string>;
  key?: string;
  value?: string;
}

export type MbDataset = MbTextDatasetEntry |
  MbPublicationDatasetEntry |
  MbImageDatasetEntry |
  MbVideoDatasetEntry |
  MbProtocolDatasetEntry |
  MbHtmlDatasetEntry |
  MbTableDatasetEntry

export interface MbProductTabExtendedValidation {
  code?: string;
  specificityEntries?: MbDataset[];
  sensitivityEntries?: MbDataset[];
}

export interface MbProductTabApplicationsDataset {
  conjugateCode?: string;
  description?: string;
  entries?: MbDataset[];
}

export interface MbProductTabApplications {
  code?: string;
  name?: string;
  applicationType?: string;
  sortOrder?: number;
  dataSets?: MbProductTabApplicationsDataset[];
}

export interface MbProductTabSpecifications {
  overviewEntries?: MbHtmlDatasetEntry[];
  detailedInfoEntries?: MbTableDatasetEntry[] | MbHtmlDatasetEntry[] | MbImageDatasetEntry[];
}

export interface MbImageGroup {
  key?: string;
  headline?: string;
  images?: MbImageDatasetEntry[];
}

export interface MbDataImageFigureEntry {
  code?: string;
  title?: string;
  longDescription?: string;
  sortOrder?: number;
  imageGroups?: MbImageGroup[];
}

export interface MbDataImageFigureEntries {
  dataImageFigures?: MbDataImageFigureEntry[];
}

export interface MbResourcesEntry {
  code?: string;
  contentType?: string;
  tagName?: string;
  longName?: string;
  sortOrder?: number;
  longUrl?: MbHtmlDatasetEntry;
}

export interface MbResourcesEntries {
  docsAndProtocols?: MbResourcesEntry[];
  certificates?: MbHtmlDatasetEntry[];
}

export interface MbQuoteInfoModel {
  flag?: boolean;
  countryIsocode?: string;
}

export interface MbCheckoutContactDetailsModel {
  title?: Salutation;
  academicDegree?: AcademicDegree;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  jobTitle?: JobTitle;
  customerNumber?: string;
}

export interface MbPaymentMode {
  code?: string;
  description?: string;
  name?: string;
  poPaymentMode?: boolean;
  preselected?: boolean;
}

export interface MbPaymentModeSelection {
  paymentMode?: MbPaymentMode;
  poNumber?: string;
}

export interface MbBillingInformation {
  billingAddress?: Address;
  paymentModeSelection?: MbPaymentModeSelection;
  paymentMode?: MbPaymentMode;
  poNumber?: string;
}

export enum MbCheckoutReviewInfoType {
  ADDRESS = 'address',
  CONTACT_DETAILS = 'contactDetails',
  SHIPMENT_METHOD = 'shipmentMethod',
  PAYMENT_METHOD = 'paymentMethod',
  PAYMENT_ADDITIONAL_INFO = 'paymentAdditionalInfo',
  NOTES = 'notes'
}

export interface MbCheckoutReviewModel {
  checkoutNotes?: string;
}

export enum MbOrderType {
  ORDER = 'order',
  REQUEST = 'request',
  MIXED = 'mixed'
}

export enum MbCheckoutRegions {
  EU = 'eu',
  US = 'us',
  UN = 'un',
  AU = 'au'
}

export enum CartType {
  ACTIVE = 'Active',
  WISH_LIST = 'WishList',
  SELECTIVE = 'Selective',
  NEW_CREATED = 'NewCreated',
}

export enum MbCheckoutTypes {
  CHECKOUT_TYPE_A = 'A',
  CHECKOUT_TYPE_B = 'B',
  CHECKOUT_TYPE_C = 'C',
  CHECKOUT_TYPE_D = 'D',
  CHECKOUT_TYPE_E = 'E',
}

export interface PoUpload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  file?: File;
  termsOfUse?: boolean;
}

export interface Wishlist extends Cart {
  name?: string;
}

declare module '@spartacus/core' {

  export interface BaseSite {
    fallback?: boolean;
    country?: Country;
    allSites?: MbBaseSiteCountry[];
    currency?: Currency;
    supportingAccountCreation?: boolean;
    supportingGuestCheckout?: boolean;
    maxAllowedQuantityPerProductInCart?: number;
  }

  export interface BaseStore {
    deliveryCountries?: Country[];
  }

  export interface Country {
    flag?: string;
    sortOrder?: number;
    flagMedia?: CmsBannerComponentMedia;
    nameNatLang?: string;
  }

  export interface Address {
    institute?: string;
    department?: string;
    building?: string;
    lab?: string;
    taxExempt?: boolean;
    vatId?: string;
    billingAddress?: boolean;
    shippingAddress?: boolean;
    streetInformation?: string;
    verificationStatus?: string;
    // Shipping Address
    company?: string;
    city?: string;
    state?: string;
    street?: string;
    houseNo?: string;
    zipcode?: string;
    // Billing Address
    salesTaxExemption?: boolean;
    isTheSameAsShipping?: boolean;
  }

  export interface VariantOption {
    productLine?: ProductLine;
    clinicalReferenceNumber?: string;
    regulatoryStatus?: string[];
    stamps?: string[];
    searchInformationMap?: { entry: { key?: string; value?: string }[] };
    name?: string;
    priceOnRequest?: boolean;
  }

  export interface Product {
    filters?: ProductVariantFilterData[];
    applicationImages?: GalleryImageData[];
    reviewsData?: MbReview;
    isAntibody?: boolean;
    originalCode?: string;
    seoTitle?: string;
    seoDescription?: string;
    priceOnRequest?: boolean;
    productLine?: ProductLine;
    searchInformationMap?: { entry: { key?: string; value?: string }[] };
    categoryNamePath?: string;
    lastCategoryName?: string;
    urlEncodedName?: string;
  }

  export interface User {
    cdcUID?: string;
    country?: {
      isocode: string;
    };
  }

  export interface ScopedProductData {
    urlEncodedName: string;
  }

  export interface ProductReference {
    reference?: string;
  }

  export interface FacetValue {
    displayName?: string;
    //HomepageFacetValue
    categoryName?: string;
    facetValue?: string;
    facetValueHash?: string;
  }

  export interface Facet {
    readonlyFacetValues?: string[];
  }

  export interface Page {
    [key: string]: any;
  }
}

declare module '@spartacus/cart/base/root' {

  export interface OrderEntry {
    entries?: OrderEntry[];
    erpPrice?: Price;
    erpPriceSingle?: Price;
    erpListPrice?: Price;
    erpBaseListPrice?: Price;
    purchasable?: boolean;
    orderEntryType?: string;
  }

  export interface ReplenishmentOrder {
    checkoutStatus?: MbCheckoutStatus;
    erpPricesAreCustomerSpecific?: boolean;
    erpPricingError?: boolean;
  }

  export interface Consignment {
    entryType?: string;
  }

  export interface OrderHistory {
    erpTotal?: Price;
    purchaseOrderCode?: string;
    type: MbOrderType;
  }

  export interface Cart {
    totalPurchaseItemsCount?: number;
    totalRequestItemsCount?: number;
    paymentDetails?: MbBillingInformation;
    checkoutNotes?: string;
    contactInformation?: MbCheckoutContactDetailsModel;
    checkoutStatus?: MbCheckoutStatus;
    country?: Country;
    erpPrice?: Price;
    erpTotalListPrice?: Price;
    erpFright?: Price;
    erpTaxValue?: Price;
    erpTotalPrice?: Price;
    removedProducts?: string[];
    erpPricingError?: boolean;
    erpPricesAreCustomerSpecific?: boolean;
  }
}

declare module '@spartacus/order/root' {

  export interface Order {
    contactInformation?: MbCheckoutContactDetailsModel;
    paymentDetails?: MbBillingInformation;
    checkoutNotes?: string;
    checkoutStatus?: MbCheckoutStatus;
    purchaseOrderCode?: String;
    requestOrderCode?: String;
    erpPrice?: Price;
    erpFright?: Price;
    erpTaxValue?: Price;
    erpTotalPrice?: Price;
    erpPricingError?: boolean;
    erpPricesAreCustomerSpecific?: boolean;
    erpTotalListPrice?: Price;
  }
}

declare module '@spartacus/core' {

  export namespace Occ {

    export interface OrderEntry {
      entries?: OrderEntry[];
    }
  }
}
